import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Barcode, Search, Pencil, Mic, Sparkles, Refrigerator, QrCode, History, ShoppingCart } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { useLoadout } from "@/context/LoadoutContext";
import YourMealsSheet from "@/components/YourMealsSheet";

/**
 * QuickTrackMenu — round "+" button in the dashboard greeting that opens a
 * small popover with the 4 fastest ways to log a meal. Every entry deep-links
 * to `/app/pasto?mode=XXX` so the AddMeal page auto-opens the right modal.
 */
export default function QuickTrackMenu({ onVoice, onSaved, date, mealType, size = "md", testIdSuffix = "" }) {
  const { t } = useT();
  const { palette } = useLoadout();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  // "down" (default) or "up" — flipped when the trigger is close to the
  // bottom of the viewport so the menu doesn't get eaten by the bottom nav.
  const [direction, setDirection] = useState("down");
  const wrapRef = useRef(null);
  const btnRef = useRef(null);

  // When placed inside a specific meal row we append &meal_type=... so the
  // AddMeal screen (or the fridge recipe dialog) starts pre-scoped to that
  // meal (breakfast/snack/lunch/afternoon/dinner).
  const mealQS = mealType ? `&meal_type=${encodeURIComponent(mealType)}` : "";

  // Close when the user taps outside the popover.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  // Flip the menu upwards when the trigger is close to the bottom of the
  // viewport — otherwise the bottom nav (fixed z-40) covers the lower
  // options ("Aus dem Kühlschrank" / "Lebensmittel") and they become
  // unreachable. Measured on open using the actual button rect.
  const handleTriggerClick = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const vh = typeof window !== "undefined" ? window.innerHeight : 800;
      // ~460px is the menu height (title + 8 rows). Bottom-nav ≈ 100px.
      const spaceBelow = vh - rect.bottom - 100;
      const spaceAbove = rect.top - 20;
      setDirection(spaceBelow < 460 && spaceAbove > spaceBelow ? "up" : "down");
    }
    setOpen((v) => !v);
  };

  const go = (mode) => {
    setOpen(false);
    // "voice" needs the mic getUserMedia to be triggered by a DIRECT user
    // gesture on the current page (iOS Safari blocks it after a nav). When
    // the parent supplies an `onVoice` callback we invoke it in-place; else
    // we fall back to the AddMeal ?mode=voice deep-link.
    if (mode === "voice" && typeof onVoice === "function") {
      onVoice(mealType);
      return;
    }
    // "recent" opens the "Deine Mahlzeiten" sheet (last 7 days) in place.
    if (mode === "recent") {
      setRecentOpen(true);
      return;
    }
    // "fridge" is a special route that opens the fridge-photo recipe dialog
    // on the Recipes page instead of the AddMeal page.
    if (mode === "fridge") {
      navigate(`/app/ricette?fridge=1${mealType ? `&meal_type=${encodeURIComponent(mealType)}` : ""}`);
      return;
    }
    // "qrscan" opens the in-app QR scanner on the Recipes page (shared
    // recipe / meal QR codes from friends).
    if (mode === "qrscan") {
      navigate("/app/ricette?qrscan=1");
      return;
    }
    if (mode === "shop") {
      navigate("/app/einkauf");
      return;
    }
    navigate(`/app/pasto${mode ? `?mode=${mode}${mealQS}` : mealType ? `?meal_type=${encodeURIComponent(mealType)}` : ""}`);
  };

  const options = [
    { key: "recent",  icon: History,      label: t("diary.yourMeals"),    color: "#2C5254" },
    { key: "voice",   icon: Mic,          label: t("quickTrack.voice"),   color: "#EF4444" },
    { key: "photo",   icon: Camera,       label: t("quickTrack.photo"),   color: "#FC9700" },
    { key: "barcode", icon: Barcode,      label: t("quickTrack.barcode"), color: "#5A8B8F" },
    { key: "shop",    icon: ShoppingCart, label: t("quickTrack.shop"),    color: "#B45309" },
    { key: "fridge",  icon: Refrigerator, label: t("quickTrack.fridge") || "Aus dem Kühlschrank", color: "#0F766E" },
    { key: "qrscan",  icon: QrCode,       label: t("quickTrack.qrScan"),  color: "#C026D3" },
    { key: "search",  icon: Search,       label: t("quickTrack.search"),  color: "#7C3AED" },
    { key: null,      icon: Pencil,       label: t("quickTrack.manual"),  color: "#10B981" },
  ];

  const isSm = size === "sm";
  const btnSize = isSm ? "h-8 w-8" : "h-11 w-11";
  const icoSize = isSm ? "h-4 w-4" : "h-5 w-5";
  const btnTid = `quick-track-btn${testIdSuffix ? `-${testIdSuffix}` : ""}`;
  const wrapTid = `quick-track-wrap${testIdSuffix ? `-${testIdSuffix}` : ""}`;
  const menuTid = `quick-track-menu${testIdSuffix ? `-${testIdSuffix}` : ""}`;

  return (
    <div ref={wrapRef} className="relative" data-testid={wrapTid}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleTriggerClick}
        data-testid={btnTid}
        aria-label={t("quickTrack.addMeal")}
        className={`${btnSize} rounded-full flex items-center justify-center text-white hover:scale-105 active:scale-95 transition-transform`}
        style={{
          background: `linear-gradient(135deg, ${palette.via}, ${palette.from})`,
          boxShadow: `0 6px 16px -4px ${palette.accent}88`,
        }}
      >
        <Sparkles className={`${icoSize} transition-transform duration-300 ${open ? "rotate-90 scale-110" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="menu"
            initial={{ opacity: 0, y: direction === "up" ? 6 : -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: direction === "up" ? 6 : -6, scale: 0.96 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute right-0 ${direction === "up" ? (isSm ? "bottom-10" : "bottom-12") : (isSm ? "top-10" : "top-12")} z-[60] w-52 max-h-[70vh] overflow-y-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#1a1d1e] shadow-[0_16px_40px_-10px_rgba(15,23,42,0.35)] p-1`}
            data-testid={menuTid}
            data-direction={direction}
          >
            <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest font-bold text-slate-400">
              {t("quickTrack.title")}
            </p>
            {options.map((o) => {
              const Ico = o.icon;
              return (
                <button
                  key={o.key || "manual"}
                  type="button"
                  onClick={() => go(o.key)}
                  data-testid={`quick-track-${o.key || "manual"}`}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 active:scale-[0.98] transition text-left"
                >
                  <span
                    className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: `${o.color}14`, color: o.color }}
                  >
                    <Ico className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-100 flex-1">
                    {o.label}
                  </span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      <YourMealsSheet
        open={recentOpen}
        onOpenChange={setRecentOpen}
        date={date}
        mealType={mealType}
        onSaved={onSaved}
      />
    </div>
  );
}
